import { Col, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import React, { useContext, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { updateOnboardingStep } from "@/apis/UserOnboardingStepHelper";
import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";
import { STEP_ID } from "@/components/constants";
import { IVVFailed } from "@/components/IdentityCheck/IVV/IVVFailed";
import VerificationCompleted from "@/components/IdentityCheck/VerificationCompleted";
import { trackSectionToggledOnce } from "@/hooks/useTrackSectionToggled";
import { useUserProgress } from "@/hooks/useUserProgress";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { Day1EventCategory, Day1EventLabel } from "@/types/snowplow-events";

const FIVE_SECONDS_IN_MILLISECONDS = 5_000;

export const DATA_TEST_ID_IVV_COMPLETED = "IDVComplete";
export const IVVCompleted = () => {
  const { dispatch, state } = useContext(AppContext);
  const {
    data: userProgress,
    status: userProgressApiStatus,
    isError: userProgressApiIsError,
  } = useUserProgress({
    refetchInterval: FIVE_SECONDS_IN_MILLISECONDS,
  });

  // Keep track of if the backend has agreed that IVV passed.
  const [backendAgreement, setBackendAgreement] = useState<boolean | undefined>(
    undefined
  );

  useEffect(() => {
    if (userProgressApiStatus === "success") {
      if (userProgress) {
        // Check if back end responded with successful verification confirmation.
        if (
          userProgress.verificationStatus.some((x) => {
            return (
              x.VerificationTypeStatus === "SUBMITTED_VERIFICATION_SUCCESS" &&
              x.VerificationType === "AUTOMATED_IVV_VERIFICATION"
            );
          })
        ) {
          updateOnboardingStep("D1_IDV_COMPLETE");
          // Track metric where this is successful.
          trackSectionToggledOnce(
            Day1EventCategory.AutomatedRivOutcome,
            Day1EventLabel.ResultPass
          );

          // IVVLegalDisclosure component disabled the button earlier, re-enable it now.
          if (state.currentStep.hideNext) {
            // show wizard next button
            dispatch({
              stepId: STEP_ID.IDENTITY_CHECK,
              type: ActionType.SET_HIDE_NEXT,
              hideNext: false,
            });
          }
          // Enable wizard next button
          dispatch({
            stepId: STEP_ID.IDENTITY_CHECK,
            type: ActionType.SET_NEXT_BTN_STATUS,
            enableNext: true,
          });

          // Show a happy status
          setBackendAgreement(true);
        }

        // Check if back end responded with rejected verification.
        if (
          userProgress?.verificationStatus.some((x) => {
            return (
              x.VerificationType === "AUTOMATED_IVV_VERIFICATION" &&
              [
                "SUBMITTED_VERIFICATION_REJECTED",
                "SUBMITTED_VERIFICATION_REJECTED_SUSPICIOUS",
              ].includes(x.VerificationTypeStatus)
            );
          })
        ) {
          setBackendAgreement(false);
        }
      }
    }

    // In error cases, show a failed message. There is refetching on error enabled by react-query so this should
    // be if it failed permanently and stopped retrying so something unrecoverable right now happened.
    if (userProgressApiIsError) {
      setBackendAgreement(false);
    }
  }, [userProgress, userProgressApiStatus, userProgressApiIsError]);

  // Set context for chat support.
  useEffect(() => {
    dispatch({
      type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
      nonDirectedChatSupportContext: "ON_IVV_PENDING",
    });

    trackSectionToggledOnce(
      Day1EventCategory.AutomatedRivOutcome,
      Day1EventLabel.WaitForResult
    );
  }, []);

  return (
    <div data-test-id={DATA_TEST_ID_IVV_COMPLETED}>
      <Spacer height={"S200"} />
      {/* We have progress but back end is still pending agreement. */}
      {userProgressApiStatus === "success" &&
        backendAgreement === undefined && (
          <div data-test-id="IVVCompletedPending">
            <Row justifyContent="center">
              <Spinner />
            </Row>
            <br />
            <Col gridGap="S400">
              <StepBodyStyledText>
                <Text textAlign="center">
                  <FormattedMessage
                    id="identityCheck-ivv-pendingBackendAgreementPre"
                    values={{
                      b: (chunks: string) => {
                        return <b>{chunks}</b>;
                      },
                    }}
                  />
                </Text>
              </StepBodyStyledText>
            </Col>
            <Col gridGap="S400">
              <StepBodyStyledText>
                <Text textAlign="center">
                  <FormattedMessage id="identityCheck-ivv-pendingBackendAgreement" />
                </Text>
              </StepBodyStyledText>
            </Col>
          </div>
        )}
      {backendAgreement && <VerificationCompleted />}
      {backendAgreement === false && <IVVFailed failedReason="REJECTED" />}
    </div>
  );
};
